import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Can } from '@/components/auth/Can';
import { TajerGoBrand } from '@/components/brand/TajerGoBrand';
import { defineAbilitiesFor } from '@/lib/abilities';
import { useAuthStore } from '@/store/authStore';
import { isNavItemVisible, navItemHref, platformSidebarGroups, type PlatformSidebarItem } from './sidebarNav';

/**
 * md+ navigation rail. Phones (<md) use PhoneNav (bottom tab bar + More sheet),
 * which filters the same platformSidebarGroups with the same abilities.
 */
export function Sidebar(): JSX.Element {
    const { t } = useTranslation();
    const location = useLocation();
    // Admin's rail is the benchmark: collapsed at rest, expanding on hover with
    // no toggle to press and nothing persisted. `collapsed` is derived rather
    // than stored, so the rail cannot be left open across a reload.
    const [isSidebarHovered, setIsSidebarHovered] = useState(false);
    const collapsed = !isSidebarHovered;
    // Touch has no hover: the first tap on the collapsed rail only expands it
    // (no navigation), the next tap navigates, and tapping outside or Esc
    // collapses it. Mouse and keyboard keep the hover / focus behaviour.
    const asideRef = useRef<HTMLElement | null>(null);
    const tapOnlyExpandsRef = useRef(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
    const role = useAuthStore((s) => s.user?.role);
    const ability = useMemo(() => defineAbilitiesFor(role), [role]);

    useEffect(() => {
        if (!isSidebarHovered) return undefined;
        const onPointerDown = (event: PointerEvent): void => {
            if (event.pointerType === 'mouse') return;
            if (asideRef.current && !asideRef.current.contains(event.target as Node | null)) setIsSidebarHovered(false);
        };
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setIsSidebarHovered(false);
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isSidebarHovered]);

    const isRouteActive = (to: string, exact?: boolean): boolean =>
        exact ? location.pathname === to : location.pathname === to || location.pathname.startsWith(`${to}/`);

    const filteredGroups = platformSidebarGroups
        .map((group) => ({
            ...group,
            items: group.items.filter((item) => {
                const query = searchTerm.trim().toLowerCase();
                if (!query) return true;
                const label = t(item.i18nKey, item.i18nKey).toLowerCase();
                const childMatch = item.children?.some((child) => t(child.i18nKey, child.i18nKey).toLowerCase().includes(query));
                return label.includes(query) || childMatch;
            }),
        }))
        .filter((group) => group.items.length > 0);

    const renderItem = (item: PlatformSidebarItem): JSX.Element | null => {
        if (!isNavItemVisible(ability, item)) return null;
        const Icon = item.icon;
        const active = isRouteActive(item.to, item.exact);
        const hasChildren = Boolean(item.children?.length);
        const open = openItems[item.to] ?? active;
        const label = t(item.i18nKey, item.i18nKey);

        return (
            <div key={item.to}>
                    {/*
                     * A11y: the expand/collapse chevron is a SIBLING of the NavLink,
                     * not nested inside it. Interactive elements must not be nested
                     * (a button inside an anchor is invalid + a screen-reader hazard).
                     */}
                    <div className="flex items-center gap-1">
                        <NavLink
                            to={navItemHref(ability, item)}
                            title={collapsed ? label : undefined}
                            aria-label={label}
                            aria-current={active ? 'page' : undefined}
                            className={cn(
                                'group relative flex min-h-11 min-w-0 flex-1 items-center rounded-xl text-sm font-semibold transition-[background-color,color,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tg-teal/50 motion-reduce:transition-none',
                                collapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2.5',
                                active
                                    ? 'bg-tg-teal text-white shadow-lg shadow-tg-teal/20'
                                    : 'text-slate-300 hover:translate-x-0.5 hover:bg-white/[0.06] hover:text-white motion-reduce:hover:translate-x-0 rtl:hover:-translate-x-0.5',
                            )}
                        >
                            <span
                                className={cn(
                                    'grid h-7 w-7 shrink-0 place-items-center rounded-lg transition-colors duration-150',
                                    active
                                        ? 'bg-white/[0.14] text-white'
                                        : 'bg-white/[0.05] text-slate-400 group-hover:bg-white/[0.08] group-hover:text-white/80',
                                )}
                            >
                                <Icon className="h-4 w-4" />
                            </span>
                            {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
                        </NavLink>
                        {!collapsed && hasChildren && (
                            <button
                                type="button"
                                onClick={() => setOpenItems((current) => ({ ...current, [item.to]: !open }))}
                                aria-label={t(open ? 'common.collapseItem' : 'common.expandItem', { item: label })}
                                aria-expanded={open}
                                className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tg-teal/50"
                            >
                                <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-150', open && 'rotate-180')} />
                            </button>
                        )}
                    </div>
                    {!collapsed && hasChildren && (
                        <div className={cn('grid transition-[grid-template-rows,opacity] duration-150 motion-reduce:transition-none', open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')}>
                            <div className="overflow-hidden">
                                <div className="ms-9 mt-1 space-y-1 border-s border-white/[0.08] ps-3">
                                    {item.children?.map((child) => (
                                        <Can key={child.to} action={child.action} subject={child.subject}>
                                            <NavLink
                                                to={child.to}
                                                className={({ isActive }) =>
                                                    cn(
                                                        'flex min-h-11 items-center truncate rounded-lg px-3 py-2 text-xs font-medium md:min-h-9 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tg-teal/50 motion-reduce:transition-none',
                                                        isActive ? 'bg-white/[0.1] text-white' : 'text-slate-300 hover:bg-white/[0.06] hover:text-white',
                                                    )
                                                }
                                            >
                                                <span className="truncate" title={t(child.i18nKey, child.i18nKey)}>
                                                    {t(child.i18nKey, child.i18nKey)}
                                                </span>
                                            </NavLink>
                                        </Can>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
            </div>
        );
    };

    const brand = () => (
        <div className={cn('flex h-16 shrink-0 items-center border-b border-white/[0.08] px-4 md:h-20', collapsed ? 'justify-center' : 'justify-between')}>
            <NavLink to="/dashboard" className={cn('flex min-w-0 items-center', collapsed ? 'justify-center' : 'gap-3')}>
                <TajerGoBrand
                    product="Platform"
                    tone="dark"
                    size="md"
                    iconOnly={collapsed}
                />
            </NavLink>
        </div>
    );

    const navItems = () => (
        <div className="flex h-full flex-col bg-tg-ink">
            {brand()}
            {!collapsed && (
                <div className="px-3 pt-3">
                    <div className="flex h-11 items-center gap-2 rounded-xl border md:h-9 border-white/[0.08] bg-white/[0.05] px-3 text-slate-300 transition-colors duration-150 focus-within:border-tg-teal/40 focus-within:bg-white/[0.08] motion-reduce:transition-none">
                        <Search className="h-4 w-4 shrink-0" />
                        <input
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder={t('nav.searchConsole')}
                            aria-label={t('nav.searchConsole', 'Search console')}
                            type="search"
                            className="min-w-0 flex-1 bg-transparent text-sm md:text-xs font-medium text-white outline-none placeholder:text-slate-400"
                        />
                        {searchTerm && (
                            <button type="button" onClick={() => setSearchTerm('')} className="grid h-9 w-9 shrink-0 place-items-center rounded-md text-slate-400 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tg-teal/50" aria-label={t('nav.clearSearch')}>
                                <X className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            )}
            <nav className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-3 pb-[max(1rem,var(--pa-safe-bottom))] pt-4" aria-label={t('nav.primary')}>
                {filteredGroups.map((group) => (
                    <div key={group.label} className="space-y-1">
                        {!collapsed && (
                            <div className="px-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                                {t(group.i18nKey, group.label)}
                            </div>
                        )}
                        {group.items.map((item) => renderItem(item))}
                    </div>
                ))}
            </nav>
        </div>
    );

    return (
        <>
            {/* Fixed, not in-flow: the expanded rail overlays the page instead of
                shoving it sideways, so content never reflows under the pointer.
                AppLayout reserves the 76px resting width. */}
            <aside
                ref={asideRef}
                // Read the rail state at touch-down: compat mouse/focus events
                // expand it before the click lands, so the click cannot tell.
                onPointerDownCapture={(event) => {
                    tapOnlyExpandsRef.current = event.pointerType !== 'mouse' && collapsed;
                }}
                onClickCapture={(event) => {
                    if (!tapOnlyExpandsRef.current) return;
                    tapOnlyExpandsRef.current = false;
                    event.preventDefault();
                    setIsSidebarHovered(true);
                }}
                onMouseEnter={() => setIsSidebarHovered(true)}
                onMouseLeave={() => setIsSidebarHovered(false)}
                // Keyboard users have no hover: expand while focus is inside the
                // rail and collapse once it tabs out to the page.
                onFocus={() => setIsSidebarHovered(true)}
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                        setIsSidebarHovered(false);
                    }
                }}
                className={cn(
                    'fixed inset-y-0 z-50 hidden flex-col border-e border-white/[0.06] transition-[width] duration-200 motion-reduce:transition-none md:flex',
                    // Logical start edge: left in LTR, right under <html dir="rtl">.
                    'start-0',
                    isSidebarHovered ? 'w-[280px] shadow-2xl' : 'w-[76px]',
                )}
            >
                {navItems()}
            </aside>

        </>
    );
}
